import _ from 'lodash';
import { RESET_STUDIO_ACTION } from '../consts';
import type { CSSSize, Size } from '../../types';
import type { ObjectDefinition } from '../../types/Schema';
import type { FieldObject } from '../../lib/FieldObject';
import type { StudioUiActions } from '../../actions/studio/ui';
import type { FieldPath } from '../../types/Field';
import type { CustomActionWithDetails, RemoteAsset, RootFieldModel } from '../../types/FieldModel';
import type { AssetSource } from '../../types/AssetSource';
import type { CreateObjectProps } from '../../modules/studio.utils';
import { HYDRATE_STATE } from '../../store/consts';
import type { SidebarButton } from '../../types/SidebarButton';
import type { InfoModalProps } from '../../components/common/InfoModal/InfoModal';
import type { UpgradeModalStandaloneProps } from '../../components/studio/UpgradeModal/UpgradeModal';
import type { CreateScheduleModalProps } from '../../components/studio/PublishDropdown/Scheduling/CreateScheduleModal';
import type { CustomActionTarget } from '../../actions/studio/custom-actions';
import type { DocumentVersionRestoreModalProps } from '../../components/studio/DocumentVersions/DocumentVersionRestoreModal';
import type { CustomFieldModalProps } from '../../components/studio/CustomField/CustomFieldModal';
import type { StudioAsset } from './assets';

export enum FRAME_BLUR_TYPE {
    CMS_PREVIEW = 'CMS_PREVIEW',
    VALIDATION = 'VALIDATION'
}

export type BlurredFrame =
    | false
    | {
          blurType: FRAME_BLUR_TYPE.CMS_PREVIEW;
          props: {
              enabled: boolean;
              isActive: boolean;
              onToggle(): void;
          };
      };

export type PreviewUpdateChange = {
    type: 'change';
    changeId: string;
};

export type PreviewUpdateCustomAction = {
    type: 'action';
    actionId: string;
};

export type PreviewUpdateType = PreviewUpdate['type'];

export type PreviewUpdate = { text?: string } & (PreviewUpdateChange | PreviewUpdateCustomAction);

export interface State {
    appMode: AppMode;
    availableStudioModes: Array<keyof typeof STUDIO_MODE>;
    studioMode: keyof typeof STUDIO_MODE | null;
    lastStudioMode: keyof typeof STUDIO_MODE | null;
    activeSeoTab: (typeof SEO_TABS)[keyof typeof SEO_TABS];
    activeModal: ActiveStudioModal | null;
    isClosingActiveModal: boolean;
    isUpgradeModalVisible: boolean;
    isRoleEditorModalVisible: boolean;
    sidebarWidth: number | string;
    footerHeight: number | string;
    isFooterExpanded: boolean;
    isDimmedFrame: boolean;
    frameBlur: BlurredFrame;
    cmsPanelLeftWidth?: number | string;
    markdownEditorSize: CSSSize;
    frameSize?: Partial<Size>;
    previewUpdating?: PreviewUpdate[];
    isDragging: boolean;
    collaboratorsPopupVisible: boolean;
    publishPopupVisible: boolean;
    siteMapVisible: boolean;
    sidebarButtons: SidebarButton[];
}

export const APP_MODE = {
    /**
     * Studio runs in the default mode. User is either owner or collaborator
     */
    STUDIO: 'STUDIO',

    /**
     * Studio runs in the preview mode without editing. Url with previewToken, should match defined for project
     * or fail. Any user can open it
     */
    PREVIEW: 'PREVIEW'
} as const;

export type AppMode = keyof typeof APP_MODE;

export const STUDIO_MODE = {
    PREVIEW: 'PREVIEW',

    /**
     * Content editing mode - sidebar has fields; outline buttons are related to content only
     */
    CMS: 'CMS',

    /**
     * Sidebar is code editor; outline buttons are related to code
     */
    CODE: 'CODE'
} as const;

export type StudioMode = keyof typeof STUDIO_MODE;

export const SEO_TABS = {
    GENERAL: 'Metadata',
    SOCIAL: 'Social',
    FEEDBACK: 'Feedback'
} as const;

export enum STUDIO_MODAL_TYPE {
    ADD_ENTITY = 'ADD_ENTITY',
    REFERENCE_ITEM = 'REFERENCE_ITEM',
    ADD_OR_REFERENCE_ITEM = 'ADD_OR_REFERENCE_ITEM',
    DUPLICATE_ENTITY = 'DUPLICATE_ENTITY',
    MARKDOWN_EDITOR = 'MARKDOWN_EDITOR',
    IMAGE_GALLERY = 'IMAGE_GALLERY',
    DELETE_OBJECT_VALIDATION = 'DELETE_OBJECT_VALIDATION',
    DELETE_OBJECT = 'DELETE_OBJECT',
    SAVE_PRESET = 'SAVE_PRESET',
    UPGRADE = 'UPGRADE',
    CONFIRM_SLUG_CHANGE = 'CONFIRM_SLUG_CHANGE',
    INFO_MESSAGE = 'INFO_MESSAGE',
    CREATE_SCHEDULE = 'CREATE_SCHEDULE',
    RUN_CUSTOM_ACTION = 'RUN_CUSTOM_ACTION',
    CUSTOM_FIELD = 'CUSTOM_FIELD',
    SNIPPET_URL_DIFFERENT_FROM_ORIGIN = 'SNIPPET_URL_DIFFERENT_FROM_ORIGIN',
    DOCUMENT_VERSION_RESTORE = 'DOCUMENT_VERSION_RESTORE'
}

type BaseCreateProps = {
    initiator?: string;
    onEntityAdded?: { (result: CreateObjectResult): void };
    defaultModel?: RootFieldModel;
    skipPreviewUpdateCheck?: boolean;
    locale?: string;
};

export type AddItemModalProps = Omit<CreateObjectProps, 'linkableObjects' | 'isUpdating'> &
    BaseCreateProps & {
        fieldPath: FieldPath;
        position?: number;
    };

export type AddItemReadOnlyProps = Omit<BaseCreateProps, 'onEntityAdded'>;

export type AddPageModalProps = BaseCreateProps & {
    modelTypes?: Array<'page' | 'data'>;
};

export type CreateObjectResult =
    | (ObjectDefinition & {
          url?: string;
          srcModelName: string;
      })
    | undefined;

export type DuplicateItemModalProps = ObjectDefinition & { srcModelName: string; onEntityAdded?: (result: CreateObjectResult) => void };

export type CustomActionModalProps = {
    action: CustomActionWithDetails;
    actionTarget?: CustomActionTarget;
    actionLocale?: string;
};

export interface GalleryModalProps {
    forceSource?: AssetSource;
    place: string;
    fieldPath: FieldPath;
    srcType: string;
    srcProjectId: string;
    srcEnvironment: string | null;
    Modal?: React.ElementType;
    saveButtonText?: string;
    showCancelButton?: boolean;
    onCancel?: { (): void };
    onSave?: { (asset: StudioAsset | RemoteAsset, assetSource: AssetSource): Promise<void> | void };
    onClose?: { (result?: $TSFixMe): void };
}

export type DeleteObjectModalProps = ObjectDefinition & {
    fieldPath?: FieldPath;
    locale?: string;
    initiator: string;
    skipPreviewUpdateCheck?: boolean;
};

export type DeleteObjectValidationProps = {
    deletionType: 'object' | 'page';
    deletionObject: ObjectDefinition;
    errors: ObjectDefinition[];
};

export type SavePresetProps = FieldObject & {
    loadPathname?: string;
    size?: Partial<Size>;
    crop?: boolean;
};

export type MarkdownEditorModalProps = {
    fieldPath: FieldPath;
    srcObjectId: string;
    srcType: string;
    srcProjectId: string;
    locale?: string;
    source: 'sidebar' | 'WYSIWYG';
    hasError?: boolean;
    onSave: (value: string) => void;
    onClose?: () => void;
    Modal?: React.ElementType;
};

export type ActiveStudioModal =
    | {
          type: STUDIO_MODAL_TYPE.ADD_ENTITY;
          props: AddPageModalProps | AddItemModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.ADD_OR_REFERENCE_ITEM;
          props: AddItemModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.REFERENCE_ITEM;
          props: AddItemModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.MARKDOWN_EDITOR;
          props: MarkdownEditorModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.IMAGE_GALLERY;
          props: GalleryModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.DELETE_OBJECT_VALIDATION;
          props: DeleteObjectValidationProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.SAVE_PRESET;
          props: SavePresetProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.DELETE_OBJECT;
          props: DeleteObjectModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.UPGRADE;
          props: UpgradeModalStandaloneProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.DUPLICATE_ENTITY;
          props: DuplicateItemModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.INFO_MESSAGE;
          props: InfoModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.CREATE_SCHEDULE;
          props: CreateScheduleModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.RUN_CUSTOM_ACTION;
          props: CustomActionModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.CUSTOM_FIELD;
          props: CustomFieldModalProps;
      }
    | {
          type: STUDIO_MODAL_TYPE.SNIPPET_URL_DIFFERENT_FROM_ORIGIN;
          props: null;
      }
    | {
          type: STUDIO_MODAL_TYPE.DOCUMENT_VERSION_RESTORE;
          props: DocumentVersionRestoreModalProps;
      };

const getDefaultState = (): State => ({
    /**
     * The mode which studio runs in now
     */
    appMode: APP_MODE.STUDIO,

    /**
     * Available studio modes
     */
    availableStudioModes: [STUDIO_MODE.CMS, STUDIO_MODE.CODE, STUDIO_MODE.PREVIEW],

    /**
     * Currently active studio mode
     */
    studioMode: null,

    /**
     * Studio mode saved during restarting and loading container
     */
    lastStudioMode: null,

    /**
     * Currently active seo tab inside advanced tools component
     */
    activeSeoTab: SEO_TABS.GENERAL,

    /**
     * Active modal
     */
    activeModal: null,

    /**
     * Active modal is being closed, used for animations to let it play nicely with existing content
     */
    isClosingActiveModal: false,

    /**
     * Upgrade modal shown
     */
    isUpgradeModalVisible: false,

    /**
     * RoleEditor modal shown
     */
    isRoleEditorModalVisible: false,

    /**
     * Sidebar width
     */
    sidebarWidth: 0,

    /**
     * Footer height
     */
    footerHeight: 0,

    /**
     * Studio footer is expanded
     */
    isFooterExpanded: false,

    /**
     * Studio iframe is dimmed
     */
    isDimmedFrame: false,

    /**
     * Studio iframe is blurred
     */
    frameBlur: false,

    /**
     * Markdown editor size
     */
    markdownEditorSize: {
        width: 1000,
        height: 560
    },

    /**
     * This flag is set to true when there is active dragging happening on the studio page.
     * Discovered, that chrome has bug when dragging happens on top of the iframe (e.g. onscreen more menu, reorder items in the child list)
     * When it's true - set's pointer-events: none to the iframe
     */
    isDragging: false,

    /**
     * Popup open/close state is managed inside DropdownPopup component and false on init.
     * Other components need to react such as Logs panel needs to close, when they are visible
     */
    collaboratorsPopupVisible: false,
    publishPopupVisible: false,
    siteMapVisible: false,
    sidebarButtons: []
});

const studioUiReducer = (state = getDefaultState(), action: StudioUiActions | null): State => {
    switch (action?.type) {
        case HYDRATE_STATE: {
            return {
                ...getDefaultState(),
                ...(action?.data.studio?.ui ?? {})
            };
        }

        case 'STUDIO_UI_SET_PREVIEW_UPDATING': {
            const { previewUpdating } = action.data;
            return {
                ...state,
                previewUpdating: previewUpdating.length > 0 ? previewUpdating : undefined
            };
        }

        case 'SET_ACTIVE_SEO_TAB': {
            const { activeSeoTab } = action.data || {};
            return {
                ...state,
                activeSeoTab
            };
        }

        case 'SET_ACTIVE_MODAL': {
            return {
                ...state,
                activeModal: action.data?.activeModal,
                isClosingActiveModal: false
            };
        }

        case 'CLOSE_ACTIVE_MODAL': {
            return {
                ...state,
                isClosingActiveModal: true
            };
        }

        case 'SET_UPGRADE_MODAL_VISIBLE': {
            const { isVisible } = action.data || {};
            const studioNotPreview = state.studioMode !== STUDIO_MODE.PREVIEW;
            return {
                ...state,
                isUpgradeModalVisible: studioNotPreview ? isVisible : false
            };
        }

        case 'SET_ROLE_EDITOR_MODAL_VISIBLE': {
            const { isVisible } = action.data || {};
            const studioNotPreview = state.studioMode !== STUDIO_MODE.PREVIEW;
            return {
                ...state,
                isRoleEditorModalVisible: studioNotPreview ? isVisible : false
            };
        }

        case 'SET_APP_MODE': {
            return {
                ...state,
                appMode: action.data.mode
            };
        }

        case 'SET_STUDIO_MODE': {
            const { mode, lastStudioMode } = action.data || {};
            const isAvailable = mode ? state.availableStudioModes.includes(mode) : true;
            return isAvailable
                ? {
                      ...state,
                      studioMode: mode,
                      lastStudioMode: lastStudioMode
                  }
                : state;
        }

        case 'SET_STUDIO_AVAILABLE_MODES': {
            const { availableModes } = action.data || {};
            return {
                ...state,
                availableStudioModes: availableModes,
                studioMode: state.studioMode && availableModes.includes(state.studioMode) ? state.studioMode : null
            };
        }

        case 'SET_SIDEBAR_WIDTH': {
            return {
                ...state,
                sidebarWidth: action.data?.width
            };
        }

        case 'SET_FOOTER_HEIGHT': {
            return {
                ...state,
                footerHeight: action.data?.height
            };
        }

        case 'SET_FOOTER_EXPANDED': {
            return {
                ...state,
                isFooterExpanded: action.data?.isExpanded
            };
        }

        case 'SET_MARKDOWN_EDITOR_SIZE': {
            return {
                ...state,
                markdownEditorSize: {
                    width: action.data?.width,
                    height: action.data?.height
                }
            };
        }

        case 'SET_DIMMED_FRAME': {
            return {
                ...state,
                isDimmedFrame: action.data?.isDimmedFrame
            };
        }

        case 'SET_BLURRED_FRAME': {
            return {
                ...state,
                frameBlur: action.data?.frameBlur
            };
        }

        case 'SET_DRAGGING': {
            return {
                ...state,
                isDragging: action.data.isDragging
            };
        }

        case 'SET_COLLABORATORS_POPUP_VISIBLE': {
            return {
                ...state,
                collaboratorsPopupVisible: action.data?.collaboratorsPopupVisible
            };
        }

        case 'SET_PUBLISH_POPUP_VISIBLE': {
            return {
                ...state,
                publishPopupVisible: action.data?.publishPopupVisible
            };
        }

        case 'SET_SITE_MAP_VISIBLE': {
            return {
                ...state,
                siteMapVisible: action.data?.siteMapVisible
            };
        }

        case 'STUDIO_UI_SET_FRAME_SIZE': {
            return {
                ...state,
                frameSize: action.data
            };
        }

        case 'STUDIO_UI_SET_CMS_PANEL_LEFT_WIDTH': {
            return {
                ...state,
                cmsPanelLeftWidth: action.data
            };
        }

        case 'STUDIO_UI_SET_SIDEBAR_BUTTONS': {
            return {
                ...state,
                sidebarButtons: action.data.buttons
            };
        }

        case RESET_STUDIO_ACTION: {
            return {
                ...getDefaultState(),
                ..._.pick(state, ['sidebarWidth', 'footerHeight', 'markdownEditorSize', 'frameSize'])
            };
        }

        default: {
            return state;
        }
    }
};

export const localStorePaths = ['ui.markdownEditorSize', 'ui.frameSize', 'ui.cmsPanelLeftWidth'];

export default studioUiReducer;
